import { useCallback, useMemo } from 'react';
import { ObservableQuery, useApolloClient } from '@apollo/client';
import { useFormContext } from 'react-hook-form';
import { groupBy } from 'utils';
import {
  useSyncDiaryToTimesheetMutation,
  DiaryEntrySyncStatus,
  DiariesForDateRangeDocument,
  SyncDiaryToTimesheetMutation
} from 'graphql/types';
import { useMessage } from 'modules/intl';
import { useFeatureFlag } from 'modules/common/hooks';
import { SmartDiaryEntry } from '../../type';
import { useSmartTimesheetContext } from '../../SmartTimesheetContextProvider';
import { useRemoveSmartEntriesSelection } from '../../hooks';
import { useSmartEntryUnselectState } from '../../hooks/useSmartEntryUnselectState';
import { splitDateRanges } from '../../utils/date-range-split';

export type SyncData = {
  entriesToSync: string[];
  entriesToUnsync: string[];
  entriesAlreadyInSync: string[];
  entriesPendingDelete: string[];
};

export type SyncError = {
  id: string;
  code: string;
  description: string;
};

export type SyncResult = {
  totalCount: number;
  successCount: number;
  errors: Record<string, SyncError[]>;
};
type onComplete = (syncResult: SyncResult) => void;

const isStatus = (status: DiaryEntrySyncStatus[]) => (
  entry: SmartDiaryEntry
): boolean => (entry.syncStatus ? status.includes(entry.syncStatus) : false);

const matchIdAndStatus = (id: string) => (status: DiaryEntrySyncStatus[]) => (
  entry: SmartDiaryEntry
): boolean => entry.id === id && isStatus(status)(entry);
const isEntrySynced = (id: string) =>
  matchIdAndStatus(id)([DiaryEntrySyncStatus.Synced]);

const syncDataKey = 'syncData';
const useRefetchQuery = () => {
  const { currentPeriod } = useSmartTimesheetContext();
  const isSmartTimesheetPerformanceEnabled = useFeatureFlag(
    'isSmartTimesheetPerformanceEnabled'
  );

  const dateRange = useMemo(
    () => ({
      fromDate: currentPeriod?.startDate,
      toDate: currentPeriod?.endDate
    }),
    [currentPeriod?.endDate, currentPeriod?.startDate]
  );

  const ranges = useMemo(
    () =>
      isSmartTimesheetPerformanceEnabled
        ? splitDateRanges(dateRange)
        : [dateRange],
    [dateRange, isSmartTimesheetPerformanceEnabled]
  );

  return useMemo(
    () =>
      ranges.map((r) => ({
        query: DiariesForDateRangeDocument,
        variables: {
          dateRange: {
            fromDate: r.fromDate,
            toDate: r.toDate
          }
        }
      })),
    [ranges]
  );
};

const mapRootSyncErrors = (
  result: SyncDiaryToTimesheetMutation | null | undefined
): Record<string, SyncError[]> => {
  const error = (result?.syncDiaryToTimesheet.error || {}) as SyncError;
  return groupBy([error], (error) => error.code || '');
};

const mapSyncErrors = (
  result: SyncDiaryToTimesheetMutation | null | undefined
): Record<string, SyncError[]> => {
  const errors =
    result?.syncDiaryToTimesheet.result?.entries
      .filter((entry) => entry.error)
      .map(
        (x) =>
          ({
            id: x.entry.id,
            ...x.error
          } as SyncError)
      ) || [];
  return groupBy(errors, (error) => error.code || '');
};

const getStatus = (
  syncData: SyncData,
  result: SyncDiaryToTimesheetMutation | null | undefined
): SyncResult => {
  if (result?.syncDiaryToTimesheet.error) {
    return {
      errors: mapRootSyncErrors(result),
      totalCount:
        syncData.entriesToSync.length +
        syncData.entriesToUnsync.length +
        syncData.entriesPendingDelete.length,
      successCount: 0
    } as SyncResult;
  }
  return {
    errors: mapSyncErrors(result),
    totalCount: result?.syncDiaryToTimesheet.result?.entries.length || 0,
    successCount:
      result?.syncDiaryToTimesheet.result?.entries.filter((x) => !x.error)
        .length || 0
  } as SyncResult;
};

export const useApplyToTimsheet = (
  filteredSmartEntries: SmartDiaryEntry[]
): {
  onApplyToTimesheet: (
    smartEntries: SmartDiaryEntry[],
    onComplete: onComplete
  ) => Promise<void>;
  loading: boolean;
  syncDataKey: string;
} => {
  const [syncToTimesheet, { loading }] = useSyncDiaryToTimesheetMutation();

  const { watch } = useFormContext();
  const formValues = watch(filteredSmartEntries.map((entry) => entry.id));
  const getSyncData = useCallback(
    (smartEntries: SmartDiaryEntry[]): SyncData => {
      const result: Record<string, boolean> = smartEntries.reduce(
        (acc, item, index) => ({
          ...acc,
          [`${item.id}`]: formValues[index] ?? false
        }),
        {}
      );

      const entriesToSync = [];
      const entriesAlreadyInSync = [];
      const entriesToUnSync = [];
      const smartEntriesMap = smartEntries.reduce((acc, entry) => {
        acc[entry.id] = entry;
        return acc;
      }, {} as Record<string, SmartDiaryEntry>);

      for (const key in result) {
        if (result[key]) {
          if (
            matchIdAndStatus(key)([
              DiaryEntrySyncStatus.NotSynced,
              DiaryEntrySyncStatus.UpdatePending
            ])(smartEntriesMap[key])
          ) {
            entriesToSync.push(key);
          } else if (
            matchIdAndStatus(key)([DiaryEntrySyncStatus.Synced])(
              smartEntriesMap[key]
            )
          ) {
            entriesToUnSync.push(key);
          }
        } else if (isEntrySynced(key)(smartEntriesMap[key])) {
          entriesAlreadyInSync.push(key);
        }
      }
      return {
        entriesToSync: entriesToSync,
        entriesToUnsync: entriesToUnSync,
        entriesAlreadyInSync: entriesAlreadyInSync,
        entriesPendingDelete: smartEntries
          .filter(isStatus([DiaryEntrySyncStatus.DeletePending]))
          .map((x) => x.id)
      } as SyncData;
    },
    [formValues]
  );
  const { removeUnselectedEntries } = useSmartEntryUnselectState();
  const refetchQueries = useRefetchQuery();
  const genericError = useMessage('smartDiary.sychMessages.fullFailureMessage');
  const client = useApolloClient();
  const { removeSmartEntriesSelection } = useRemoveSmartEntriesSelection();
  const onApplyToTimesheet = useCallback(
    async (smartEntries: SmartDiaryEntry[], onComplete: onComplete) => {
      const syncData = getSyncData(smartEntries);
      let status: SyncResult;
      try {
        const syncResults = await syncToTimesheet({
          variables: {
            input: {
              diaryEntryIdsToSync: syncData.entriesToSync,
              diaryEntryIdsToUnsync: [
                ...syncData.entriesToUnsync,
                ...syncData.entriesPendingDelete
              ]
            }
          },
          awaitRefetchQueries: true,
          refetchQueries: refetchQueries
        });

        status = getStatus(syncData, syncResults.data);
      } catch (error: any) {
        status = getStatus(syncData, {
          syncDiaryToTimesheet: {
            error: { code: error.statusCode, description: genericError! }
          }
        });
        await client.refetchQueries({
          include: refetchQueries.map((q) => q.query),
          onQueryUpdated: (query: ObservableQuery<any>) => {
            if (query.variables?.dateRange) {
              return true;
            }
            return false;
          }
        });
      }

      if (status.successCount) {
        sessionStorage.setItem(syncDataKey, JSON.stringify(status));
      }
      removeUnselectedEntries(syncData.entriesToSync);
      removeSmartEntriesSelection([
        ...syncData.entriesToSync,
        ...syncData.entriesToUnsync,
        ...syncData.entriesPendingDelete,
        ...syncData.entriesAlreadyInSync
      ]);

      onComplete(status);
    },
    [
      getSyncData,
      syncToTimesheet,
      refetchQueries,
      removeSmartEntriesSelection,
      removeUnselectedEntries,
      genericError,
      client
    ]
  );

  return { onApplyToTimesheet, loading, syncDataKey };
};
